import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Empty defaults - no hardcoded text shown before data loads
const defaultSettings = {
  app_name: '',
  logo: '',
  favicon: '',
  colors: {
    primary: '',
    primary_hover: '',
    accent: '',
    background: '',
    card: '',
    card_border: '',
    text_primary: '',
    text_secondary: '',
    text_muted: ''
  }
};

const SiteContext = createContext(null);

export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within SiteProvider');
  }
  return context;
};

// Alias for useSite
export const useSiteSettings = useSite;

export const SiteProvider = ({ children }) => {
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    // Only apply theme after data is loaded
    if (!loading && settings.colors?.background) {
      applyTheme(settings.colors);
    }
    
    // Note: Document title is managed by SEOHead component per-page
    // SiteContext only sets default title when no SEOHead is rendered
    // This is handled through the Helmet defaultTitle prop
    
    // Update favicon if provided from site settings
    if (settings.favicon) {
      // Only update/create a separate shortcut icon link, don't touch the manifest/wallet provider icons
      let link = document.querySelector("link[rel='shortcut icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'shortcut icon';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      link.type = 'image/x-icon';
      link.href = settings.favicon;
      
      // Also update the primary favicon
      const primaryIcon = document.querySelector("link[rel='icon'][sizes='32x32']");
      if (primaryIcon) {
        primaryIcon.href = settings.favicon;
      }
    }
  }, [settings, loading]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/site-settings`);
      if (res.data) {
        setSettings(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch site settings');
    } finally {
      setLoading(false);
    }
  };

  const applyTheme = (colors) => {
    if (!colors) return;
    
    const root = document.documentElement;
    
    // Set CSS variables only if values exist
    if (colors.primary) root.style.setProperty('--color-primary', colors.primary);
    if (colors.primary_hover) root.style.setProperty('--color-primary-hover', colors.primary_hover);
    if (colors.accent) root.style.setProperty('--color-accent', colors.accent);
    if (colors.background) root.style.setProperty('--color-background', colors.background);
    if (colors.card) root.style.setProperty('--color-card', colors.card);
    if (colors.card_border) root.style.setProperty('--color-card-border', colors.card_border);
    if (colors.text_primary) root.style.setProperty('--color-text-primary', colors.text_primary);
    if (colors.text_secondary) root.style.setProperty('--color-text-secondary', colors.text_secondary);
    if (colors.text_muted) root.style.setProperty('--color-text-muted', colors.text_muted);
    
    // Set background color on body
    if (colors.background) {
      document.body.style.backgroundColor = colors.background;
    }
  };

  const updateSettings = (newSettings) => {
    setSettings(newSettings);
  };

  return (
    <SiteContext.Provider value={{ settings, loading, updateSettings, fetchSettings }}>
      {children}
    </SiteContext.Provider>
  );
};

export default SiteContext;
